import _sortBy from 'lodash/sortBy';
import { baseDataZoom, EChartsOption, formatValue, TNumberValue } from 'volta/components/bv-chart';
import { BufferIconBase64, extractLabels, mapSeries } from 'volta/utils/chart-utils';
import { getColor, SemanticColors } from 'volta/utils/style-utils';

import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';

import type IntlService from 'ember-intl/services/intl';
import type AppNavState from 'volta/services/app-nav-state';

type TChart =
  | 'buffer'
  | 'onHand'
  | 'avgOnHand'
  | 'availableStock'
  | 'demandQty'
  | 'openSupplyQty'
  | 'adu'
  | 'simulatedBalance';

type TSeriesKey =
  | 'bufferRedBase'
  | 'bufferRedSafety'
  | 'bufferYellow'
  | 'bufferGreen'
  | 'onHand'
  | 'avgOnHand'
  | 'availableStock'
  | 'demandQty'
  | 'openSupplyQty'
  | 'adu'
  | 'simulatedBalance'
  | 'bufferTOR'
  | 'bufferTOY'
  | 'bufferTOG';

type TSeriesInput = {
  inventoryDate: string[];
} & Record<TSeriesKey, TNumberValue[] | undefined>;

type TPlanningDefs = TSeriesDefinitions<TChart, TSeriesKey>;

const SeriesDefinitions = (isBuffered: boolean = true, theme: 'light' | 'dark') => {
  const defs: TPlanningDefs = {};

  if (isBuffered) {
    defs['buffer'] = {
      withArea: true,
      legendIcon: BufferIconBase64,
      legendLabel: 'buffer',
      series: {
        bufferRedBase: {
          label: 'redBase',
          color: getColor('critical'),
          excludeFromTooltip: true
        },
        bufferRedSafety: {
          label: 'redSafety',
          color: getColor('danger'),
          tooltipOverride: { data: 'bufferTOR', label: 'TOR' }
        },
        bufferYellow: {
          label: 'yellow',
          color: getColor('warning'),
          tooltipOverride: { data: 'bufferTOY', label: 'TOY' }
        },
        bufferGreen: {
          label: 'green',
          color: getColor('success'),
          tooltipOverride: { data: 'bufferTOG', label: 'TOG' }
        }
      }
    };
  }

  return {
    ...defs,
    onHand: {
      legendLabel: 'onHand',
      series: {
        onHand: {
          label: 'onHand',
          color: SemanticColors.bvText(theme)
        }
      }
    },
    avgOnHand: {
      hidden: true,
      series: {
        avgOnHand: {
          label: 'avgTargetOnHand',
          color: SemanticColors.bvTextSubdued(theme),
          dashed: true
        }
      }
    },
    availableStock: {
      legendLabel: isBuffered ? 'netFlow' : 'netNeeds',
      series: {
        availableStock: {
          label: isBuffered ? 'netFlow' : 'netNeeds',
          color: getColor('primary'),
          dashed: true
        }
      }
    },
    demandQty: {
      hidden: true,
      legendLabel: 'demandQty',
      series: {
        demandQty: {
          label: 'demandQty',
          color: getColor('indigo'),
          dashed: true,
          abs: true
        }
      }
    },
    openSupplyQty: {
      hidden: true,
      legendLabel: 'supplyQty',
      series: {
        openSupplyQty: {
          label: 'supplyQty',
          color: getColor('orange')
        }
      }
    },
    adu: {
      hidden: true,
      legendLabel: 'adu',
      series: {
        adu: {
          label: 'adu',
          color: getColor('purple'),
          abs: true
        }
      }
    },
    simulatedBalance: {
      hidden: true,
      legendLabel: 'simulatedBalance',
      series: {
        simulatedBalance: {
          label: 'simulatedBalance',
          color: getColor('blue'),
          dashed: true
        }
      }
    }
  } as TPlanningDefs;
};

interface IArgs {
  charts: TChart[];
  data?: TSeriesInput;
  isBuffered?: boolean;
  loading: boolean;
}

export default class PlanningChart extends Component<IArgs> {
  @service intl!: IntlService;
  @service appNavState!: AppNavState;

  get defs() {
    const { charts = [], isBuffered } = this.args;
    const defs = {} as TPlanningDefs;
    const DefaultDefs = SeriesDefinitions(isBuffered, this.appNavState.appliedTheme);

    if (charts.length) {
      charts.forEach((c) => {
        if (c) {
          const opts = DefaultDefs[c];
          if (opts) {
            defs[c] = { ...opts };
          }
        }
      });
      return defs;
    } else {
      return { ...DefaultDefs };
    }
  }

  @cached
  get labels() {
    const { defs, intl } = this;
    return extractLabels(defs, intl);
  }

  @cached
  get data() {
    const { data } = this.args;
    const { defs, labels } = this;

    const keys = Object.keys(defs);

    if (data && keys.length && data?.inventoryDate?.length) {
      const result = {} as Record<string, (string | TNumberValue)[]>;
      const { inventoryDate } = data;

      mapSeries<TChart, TSeriesKey>(defs, (_ck, _co, seriesKey, seriesOpt) => {
        const dataForKey = data[seriesKey];
        const tooltipKey = seriesOpt.tooltipOverride?.data;
        inventoryDate.forEach((_, index) => {
          const label = labels[seriesKey];
          const value = dataForKey?.[index];
          const tooltipValue = tooltipKey ? data[tooltipKey]?.[index] : undefined;

          if (!result[label]) {
            result[label] = [];
          }

          result[label]?.push(formatValue(value as number, seriesOpt.abs));

          if (tooltipKey) {
            const tooltipLabel = labels[tooltipKey];
            if (!result[tooltipLabel]) {
              result[tooltipLabel] = [];
            }

            result[tooltipLabel]?.push(formatValue(tooltipValue as number, seriesOpt.abs));
          }
        });
      });

      result['inventoryDate'] = inventoryDate;
      return result;
    }

    return undefined;
  }

  get chartOptions(): EChartsOption {
    const { defs, data, labels } = this;

    if (!data) {
      return { series: [] };
    }

    const series: EChartsOption[] = [];
    const today = new Date().toISOString();

    const legendData: { selected: Record<string, boolean>; data: object[] } = {
      selected: {},
      data: []
    };

    mapSeries<TChart, TSeriesKey>(defs, (chartKey, chartOpt, seriesKey, seriesOpt, _index) => {
      const chartLabel = labels[chartKey];
      const areaStyle = chartOpt.withArea ? { color: seriesOpt.color } : undefined;
      const name = labels[seriesKey];
      legendData.selected[name] = !chartOpt?.hidden;
      legendData.data.push({
        name: chartOpt.withArea ? chartLabel : name,
        icon: chartOpt.legendIcon
      });
      series.push({
        id: seriesKey,
        name: chartOpt.withArea ? chartLabel : name,
        type: 'line',
        showSymbol: false,
        color: seriesOpt.color,
        step: chartOpt.withArea && 'middle',
        stack: chartOpt.withArea && chartLabel,
        tooltip: {
          show: !seriesOpt.excludeFromTooltip
        },
        encode: {
          x: 'inventoryDate',
          y: name,
          tooltip: seriesOpt.tooltipOverride
            ? (seriesKey === 'bufferRedSafety' ? [labels['bufferRedBase']] : []).concat([
                name,
                labels[seriesOpt.tooltipOverride.data]
              ])
            : undefined
        },
        lineStyle: {
          type: seriesOpt.dashed ? [3, 3] : undefined,
          width: chartOpt.withArea ? 1 : 2
        },
        areaStyle
      });
    });

    series.push({
      name: 'today',
      type: 'line',
      tooltip: {
        show: false
      },
      data: [],
      markLine: {
        symbol: 'none',
        label: {
          formatter: () => this.intl.t('today')
        },
        data: [
          {
            silent: true,
            xAxis: today
          },
          {
            silent: true,
            xAxis: today
          }
        ]
      }
    });

    return {
      dataset: {
        dimensions: ['inventoryDate'].concat(Object.values(labels)),
        source: data
      },
      legend: {
        selected: legendData.selected,
        data: legendData.data
      },
      xAxis: {},
      tooltip: {},
      dataZoom: baseDataZoom(),
      series
    } as EChartsOption;
  }
}
